import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import ExportToExcelButton from '@components/ui/ExportToExcelButton';
import PageLayout from '@components/layout/PageLayout';
import styles from './SppReportsPostProdStatNetGenPage.module.css';
import { useSppReportsPostProdStatNetGenPage, SPPMeterDataWithComparison } from './useSppReportsPostProdStatNetGenPage';
import useSession from '@hooks/useSession';
import { withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

const SppReportsPostProdStatNetGenPage = () => {
  const { currentYear, navigateYear, isNextYearDisabled, data, loading, error, handleExportToExcel } = useSppReportsPostProdStatNetGenPage();
  const { isUser, isManager, isAdmin, isSuperAdmin } = useSession();
  const canExport = isUser || isManager || isAdmin || isSuperAdmin;

  if (error) {
    return (
      <PageLayout title="SPP - Post Prod Stat Generation Amounts Report">
        <div className={styles.content}>
          <p data-testid="report-error">Error loading data: {error.message}</p>
        </div>
      </PageLayout>
    );
  }

  // Format number with commas
  const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString('en-CA', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Check if post value differs from prod value
  const isDifferent = (postValue: number | null | undefined, prodValue: number | null | undefined) => {
    if (postValue === null || postValue === undefined) return false;
    if (prodValue === null || prodValue === undefined) return false;
    return postValue !== prodValue;
  };

  // Render cell with conditional red color
  const renderCell = (postValue: number | null | undefined, prodValue: number | null | undefined) => {
    const formatted = formatNumber(postValue);
    if (isDifferent(postValue, prodValue)) {
      return <span className={styles.diffCell} data-testid="report-diff-value" data-different="true">{formatted}</span>;
    }
    return formatted;
  };

  // Define columns
  const columns: ColumnsType<SPPMeterDataWithComparison> = [
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      width: 120,
      fixed: 'left',
      ellipsis: true,
    },
    {
      title: 'Name',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      fixed: 'left',
      ellipsis: true,
    },
    {
      title: 'Meter Number',
      dataIndex: 'meter_number',
      key: 'meter_number',
      width: 130,
      fixed: 'left',
      render: (value: string, record) => (
        <Link to={`/meterreaddetails/${record.meter_id}`} data-testid="meter-number-link">{value}</Link>
      ),
    },
    {
      title: 'Generation Type',
      dataIndex: 'generation_type',
      key: 'generation_type',
      width: 130,
      fixed: 'left',
    },
    {
      title: 'Jan',
      dataIndex: 'jan',
      key: 'jan',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.jan),
    },
    {
      title: 'Feb',
      dataIndex: 'feb',
      key: 'feb',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.feb),
    },
    {
      title: 'Mar',
      dataIndex: 'mar',
      key: 'mar',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.mar),
    },
    {
      title: 'Apr',
      dataIndex: 'apr',
      key: 'apr',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.apr),
    },
    {
      title: 'May',
      dataIndex: 'may',
      key: 'may',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.may),
    },
    {
      title: 'Jun',
      dataIndex: 'jun',
      key: 'jun',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.jun),
    },
    {
      title: 'Jul',
      dataIndex: 'jul',
      key: 'jul',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.jul),
    },
    {
      title: 'Aug',
      dataIndex: 'aug',
      key: 'aug',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.aug),
    },
    {
      title: 'Sep',
      dataIndex: 'sep',
      key: 'sep',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.sep),
    },
    {
      title: 'Oct',
      dataIndex: 'oct',
      key: 'oct',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.oct),
    },
    {
      title: 'Nov',
      dataIndex: 'nov',
      key: 'nov',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.nov),
    },
    {
      title: 'Dec',
      dataIndex: 'dec',
      key: 'dec',
      width: 120,
      align: 'right',
      render: (value, record) => renderCell(value, record.prod_stat_netgen.dec),
    },
    {
      title: 'YTD Total',
      dataIndex: 'ytd_total',
      key: 'ytd_total',
      width: 140,
      align: 'right',
      render: (value) => formatNumber(value),
    },
  ];

  const testColumns = withTestColumnAttrs(columns, 'report-value');

  // Render a single table for a program type
  const renderTable = (title: string, dataSource: SPPMeterDataWithComparison[], tableKey: string) => (
    <div key={tableKey} className={styles.tableSection} data-testid="report-section" data-section={tableKey}>
      <h2 className={styles.tableTitle} data-testid="report-section-title">{title}</h2>
      <Table
        columns={testColumns}
        dataSource={dataSource}
        loading={loading}
        rowKey="meter_id"
        pagination={false}
        scroll={{ x: 'max-content', y: 400 }}
        bordered
        size="small"
        locale={{ emptyText: `No ${title} meters found for this period` }}
        data-testid="report-table"
        data-section={tableKey}
        data-loading={String(loading)}
        data-row-count={String(dataSource.length)}
        onRow={(record: SPPMeterDataWithComparison) =>
          ({
            'data-testid': 'report-row',
            'data-meter-id': String(record.meter_id),
            'data-meter-number': record.meter_number ?? '',
          }) as TestAttrs
        }
      />
    </div>
  );

  return (
    <PageLayout
      title="SPP - Post Prod Stat Generation Amounts Report"
      headerAccessoryView={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {canExport && (
            <ExportToExcelButton
              onClick={handleExportToExcel}
              disabled={loading}
            />
          )}
          <div className={styles.yearNavigation}>
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() => navigateYear(-1)}
              className={styles.navBtn}
              data-testid="year-prev-button"
            />
            <span className={styles.yearLabel} data-testid="year-label" data-year={String(currentYear)}>
              {currentYear}
            </span>
            <Button
              type="text"
              icon={<RightOutlined />}
              onClick={() => navigateYear(1)}
              disabled={isNextYearDisabled}
              className={styles.navBtn}
              data-testid="year-next-button"
            />
          </div>
        </div>
      }
    >
      <div className={styles.content}>
        {renderTable('IPP', data.ippMeters, 'ipp')}
        {renderTable('COMFIT', data.comfitMeters, 'comfit')}
        {renderTable('AGC Wind Farm', data.maxcapMeters, 'maxcap')}
        {renderTable('Community Solar', data.communitySolarMeters, 'community-solar')}
      </div>
    </PageLayout>
  );
};

export default SppReportsPostProdStatNetGenPage;
